import {
  AloftAreaJSON,
  BasementAtticFinishType,
  PROPERTY_GRID_SUBJECT_PROPERTY,
  normalizeBasementAreas,
} from '@aloft/toolkit-shared';

import { useEffect } from 'react';
import { FieldPath, useWatch } from 'react-hook-form';

import { useAppDispatch } from '../../../../redux/hooks.ts';
import { PropertyGridForm } from '../../types.ts';
import { handleUpdatePropertyData } from '../../utils.ts';
import { EditableEnumField } from '../EditableEnumField.tsx';
import { PropertyGridFieldProps } from '../types.ts';
import { resolveFieldName } from '../utils.tsx';

/**
 * Wraps EditableEnumField for `basement.type` so that, whenever Type, totalArea, or
 * finishedArea change (through this field, the sibling area fields, or any other mechanism
 * that writes to the form), `basement.totalArea`/`finishedArea` are immediately re-derived via
 * `normalizeBasementAreas` and persisted, keeping this row's stored improvements data
 * consistent with what the (now disabled) sibling area fields display.
 * See BasementTotalAreaField / BasementFinishedAreaField for the disabled-cell side of this.
 */
export const BasementTypeField = (props: PropertyGridFieldProps) => {
  const { columnIndex, field, isArv = false, control, getValues, setValue } = props;
  const dispatch = useAppDispatch();

  const propertyId = getValues(`columns.${columnIndex}.propertyId`);
  const isSubject = propertyId === PROPERTY_GRID_SUBJECT_PROPERTY;
  const { propertyDataFieldName: typeFieldName } = resolveFieldName(
    field.fieldName,
    columnIndex,
    isSubject,
    isArv,
  );
  const totalAreaFieldName = typeFieldName.replace(
    /\.type$/,
    '.totalArea',
  ) as FieldPath<PropertyGridForm>;
  const finishedAreaFieldName = typeFieldName.replace(
    /\.type$/,
    '.finishedArea',
  ) as FieldPath<PropertyGridForm>;

  const basementType = useWatch({ control, name: typeFieldName }) as
    | BasementAtticFinishType
    | undefined;
  const totalArea = useWatch({ control, name: totalAreaFieldName }) as AloftAreaJSON | undefined;
  const finishedArea = useWatch({ control, name: finishedAreaFieldName }) as
    | AloftAreaJSON
    | undefined;

  useEffect(() => {
    if (!basementType) return;

    const normalized = normalizeBasementAreas({ type: basementType, totalArea, finishedArea });
    const isUnchanged =
      JSON.stringify(normalized.totalArea) === JSON.stringify(totalArea) &&
      JSON.stringify(normalized.finishedArea) === JSON.stringify(finishedArea);
    if (isUnchanged) return;

    setValue(totalAreaFieldName, normalized.totalArea as never, { shouldDirty: true });
    setValue(finishedAreaFieldName, normalized.finishedArea as never, { shouldDirty: true });

    const propertyData = getValues(`columns.${columnIndex}.propertyData`);
    handleUpdatePropertyData(propertyId, getValues(), dispatch, propertyData);
    // Re-run whenever Type, totalArea, or finishedArea change so that editing the area
    // fields directly (not just switching Type) keeps totalArea/finishedArea consistent.
    // The isUnchanged guard above prevents this from looping after it writes its own values.
  }, [basementType, totalArea, finishedArea]);

  return <EditableEnumField {...props} />;
};
